import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { CategoriesOptions } from 'components/Categories';
import { statusOptions } from 'keys/statuses';
import { semesterAndYearOptions } from "keys/years";
import { semesters } from "keys";
import { getSemesterAndYearLabel } from "components/SelectSemesterAndYear/SelectSemesterAndYear";
import qc from "keys/queryClient";
import SelectSemesterAndYear from "components/SelectSemesterAndYear";

/**
 * Hook מותאם אישית המשמש כ"רשם" (Registry) עבור כל הפילטרים במערכת.
 * תפקידו לספק אובייקט קונפיגורציה אחיד לכל פילטר, שבעזרתו ה-UI יודע איך לרנדר את רכיב הבחירה (למשל AutoComplete),
 * אילו אפשרויות להציג, ומאיפה לשאוב את הנתונים.
 *
 * @param {Object} params - אובייקט פרמטרים.
 * @param {Object} [params.assessmentDefs] - נתוני מבדקים (מכיל את רשימת המבדקים `data` ומצב טעינה `isLoading`) המגיעים לרוב מקריאת API.
 * @param {Object} [params.values] - הערכים הנוכחיים של הפילטרים (הכנה לשימוש עתידי או להשפעה של בחירה אחת על אפשרויות של פילטר אחר).
 * @returns {Object} אובייקט מילון (Dictionary) שבו כל מפתח הוא שם השדה בטופס, והערך הוא ההגדרות הוויזואליות והלוגיות שלו.
 */
const useFilterRegistry = ({ assessmentDefs } = {}) => {
    // 1. גישה למטמון (Cache) של React Query כדי לשלוף נתונים גלובליים שכבר נטענו
    const queryClient = useQueryClient()
    const batteries = queryClient.getQueryData([qc.batteries])
    const users = queryClient.getQueryData([qc.userOptions])

    // 2. משיכת נתונים מתוך ה-State הגלובלי של האפליקציה (Redux)
    const { grades } = useSelector(state => state.testTaker)

    // 3. החזרת הרשם: כל אובייקט כאן מייצג פילטר אחד עם ההגדרות שלו למערכת הטפסים/תצוגה
    return {
        school_year: {
            id: 1,
            placeholder: "שנת לימודים",
            name: "school_year",
            component: "autoComplete",
            options: semesterAndYearOptions, // נתונים סטטיים מהגדרות המערכת
            width: 200,
            minWidth: 200,
        },
        semester: {
            id: 2,
            placeholder: "סמסטר",
            name: "semester",
            options: semesters,
            component: "autoComplete",
            width: 200,
            minWidth: 200,
        },
        semester_and_year: {
            id: 3,
            placeholder: "שנה וסמסטר ביצוע",
            name: "semester_and_year",
            component: "autoComplete",
            width: 235,
            minWidth: 235,
            getOptionLabel: getSemesterAndYearLabel, // פונקציה מותאמת אישית לתצוגת הטקסט
            paperComponent: SelectSemesterAndYear // רכיב UI מותאם אישית להצגת הרשימה הנגללת
        },
        grade_of_execution: {
            id: 4,
            placeholder: "שכבה בעת ביצוע",
            options: grades, // נתונים שנמשכו מ-Redux
            name: "grade_of_execution",
            component: "autoComplete",
            width: 170,
            minWidth: 170
        },
        categories: {
            id: 5,
            placeholder: "סוג מבדק",
            name: "categories",
            component: "autoComplete",
            labelKey: "label",
            multiple: true,
            width: 280,
            readOnlyInput: true,
            paperComponent: CategoriesOptions
        },
        assessment_defs: {
            id: 6,
            placeholder: "מבדק",
            options: assessmentDefs?.data, // מבוסס על ה-Prop שעבר מבחוץ (חיבור לשרת)
            loading: assessmentDefs?.isLoading, // סנכרון מצב הטעינה
            name: "assessment_defs",
            component: "autoComplete",
            multiple: true,
            width: 250
        },
        status: {
            id: 7,
            name: "status",
            placeholder: "סטטוס מבדק",
            component: "autoComplete",
            multiple: true,
            width: 200,
            minWidth: 200,
            options: statusOptions
        },
        battery: {
            id: 8,
            name: "battery",
            placeholder: "סוללה",
            component: "autoComplete",
            options: batteries, // נתונים שנמשכו מ-React Query
            labelKey: "label",
            minWidth: 300
        },
        profile: {
            id: 9,
            name: "profile",
            placeholder: "דירוג",
            component: "autoComplete",
            multiple: true,
            width: 180,
            minWidth: 180,
            // נתונים שהוגדרו Hard-coded
            options: [{ id: 1, name: "1" }, { id: 2, name: "2" }, { id: 3, name: "3" }, { id: 4, name: "4" }, { id: 5, name: "5" }],
        },
        activated_by: {
            id: 10,
            name: "activated_by",
            placeholder: "על ידי מי הופעל?",
            component: "autoComplete",
            width: 200,
            options: users, // נתונים שנמשכו מ-React Query
            labelKey: "full_name"
        }
    };
};

export default useFilterRegistry;